import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFCMToken } from '../../../lib/firebase';
import { usePasswordRegisterMutation } from '../../../features/api/apiSlice';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../../features/auth/authSlice';
import {
  PORTAL,
  assertPortalAccess,
  buildAuthUserPayload,
} from '../../../lib/authPortals';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ROLE_OPTIONS = [
  {
    value: 'agent',
    label: 'Property Agent',
    description: 'Represent clients and respond to property needs',
  },
  {
    value: 'broker',
    label: 'Broker',
    description: 'Facilitate transactions and match seekers with listings',
  },
  {
    value: 'advisor',
    label: 'Property Advisor',
    description: 'Provide advisory services and investment guidance',
  },
];

/** Professional-only registration (agent / broker / advisor). */
const ProfessionalRegistrationForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [passwordRegister] = usePasswordRegisterMutation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    accountRole: 'broker',
    companyName: '',
    licenseNumber: '',
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!['agent', 'broker', 'advisor'].includes(formData.accountRole)) {
      newErrors.accountRole = 'Select your role';
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const displayName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();

      let fcmToken = '';
      try {
        fcmToken = (await getFCMToken()) || '';
      } catch {
        fcmToken = '';
      }

      const response = await passwordRegister({
        name: displayName,
        email: formData.email.trim(),
        password: formData.password,
        mobile: formData.phone.trim(),
        fcm_id: fcmToken,
        portal: 'professional',
        account_role: formData.accountRole,
        company_name: formData.companyName || '',
        license_number: formData.licenseNumber || '',
      }).unwrap();

      if (response?.error === true) {
        throw new Error(response?.message || 'Registration failed');
      }

      const apiUser = response.user || response.data || {};
      const authUser = buildAuthUserPayload({
        apiUser: {
          ...apiUser,
          account_role: formData.accountRole,
          company_name: formData.companyName,
        },
        fallback: { name: displayName, email: formData.email.trim() },
      });

      const access = assertPortalAccess(authUser.account_role, PORTAL.PROFESSIONAL);
      if (!access.ok) {
        setErrors({ submit: access.message });
        return;
      }

      dispatch(
        setCredentials({
          user: authUser,
          token: response.token,
          customerId: response.customerId || apiUser.id,
        })
      );

      navigate('/agent-dashboard/verification', {
        state: {
          message:
            'Real Estate Agent account created. Complete verification to earn your trust badge and respond on the notice board.',
        },
      });
    } catch (error) {
      console.error('Pro registration failed:', error);
      let message = 'Registration failed. Please try again.';
      if (error?.data?.message) {
        message = error.data.message;
      } else if (error?.message) {
        message = error.message;
      }
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-6 rounded-lg border border-secondary/30 bg-secondary/10 p-4">
        <p className="text-sm font-medium text-foreground flex items-center gap-2">
          <Icon name="Briefcase" size={16} className="text-secondary" />
          Real Estate Agent account
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          For agents, brokers, and advisors only. Looking for a property?{' '}
          <Link to="/register" className="text-primary font-medium underline">
            User registration
          </Link>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <Select
          label="I am a..."
          options={ROLE_OPTIONS}
          value={formData.accountRole}
          onChange={(v) => handleInputChange('accountRole', v)}
          error={errors.accountRole}
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            error={errors.firstName}
            required
          />
          <Input
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            error={errors.lastName}
            required
          />
        </div>

        <Input
          label="Work Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          error={errors.email}
          required
        />
        <Input
          label="Phone Number"
          type="tel"
          placeholder="+256 7XX XXX XXX"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          error={errors.phone}
          required
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label="Company / Agency"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="Trading name"
          />
          <Input
            label="License number"
            value={formData.licenseNumber}
            onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={errors.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>
        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            error={errors.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>

        <Checkbox
          label="I agree to the Terms of Service, Privacy Policy, and code of conduct"
          checked={formData.agreeToTerms}
          onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
          error={errors.agreeToTerms}
          required
        />

        {errors.submit && (
          <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
            <p className="text-sm text-error">{errors.submit}</p>
          </div>
        )}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting}>
          {isSubmitting ? 'Creating account…' : 'Create Real Estate Agent Account'}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Already registered?{' '}
          <Link to="/pro/login" className="text-primary font-medium hover:underline">
            Real Estate Agent login
          </Link>
        </p>
      </form>
    </div>
  );
};

export default ProfessionalRegistrationForm;
